import { RequestPoller } from '../../extract/services/RequestPoller';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { ERROR_NOTIFYING_SERVER_RESPONSE_DELAY } from '../../httpClient/WebHttpClientWithErrorToast';
import { ILongRunningAction, LongRunningActionState, LongRunningActionType } from '../models/ILongRunningAction';
import { GetActionInfoRequest, IGetActionInfoParams } from '../ServerApi/requests/GetActionInfoRequest';
import { IActionInfo, IGetActionInfoResult } from '../ServerApi/types/IActionInfo';
import { IActionResult } from '../ServerApi/types/IActionResult';
import { ErrorReason } from '../../sessionManagement/SessionManagementService';

class LongRunningActionService {

  private StringToStateDict: {[stringType: string]: LongRunningActionState} = {
    queued : LongRunningActionState.Queued,
    inProgress : LongRunningActionState.InProgress,
    completed: LongRunningActionState.Complete
  };

  private StringToTypeDict: {[stringType: string]: LongRunningActionType} = {
    deleteSites: LongRunningActionType.DeleteSites,
  };

  /**
   * This function tracks the progress of a LongRunningAction of the given `actionId` by polling periodically.
   * Takes optional callbacks:
   *    successCallback - triggered when the LongRunningAction is complete,
   *    failureCallback - triggered when some error occurs, or when there is no action of the given id,
   *    notifyCallback - triggered when the LongRunningAction is not yet complete.
   * Also takes a boolean to indicate if the successCallback need to be triggered with delay, which is true by default.
   * Returns the RequestPoller instance which has a cancelPolling method to stop polling if needed.
   */
  public trackAsyncJobProgress(
    actionId: string,
    successCallback?: (result: ILongRunningAction) => void,
    failureCallback?: (reason: ErrorReason) => void,
    notifyCallback?: (result: ILongRunningAction) => void,
    delayResultForSolr: boolean = true,
  ): RequestPoller<IGetActionInfoParams, IGetActionInfoResult> {
    const request = new GetActionInfoRequest({ actionId });

    const isComplete = (result: IGetActionInfoResult) => {
      if (!result?.actions?.length) {
        return true;
      } else {
        return this.StringToStateDict[result.actions[0].state] === LongRunningActionState.Complete;
      }
    };

    const successCallbackWrapped = (result: IGetActionInfoResult) => {
      if (!result?.actions?.length) {
        failureCallback && failureCallback(ErrorReason.ResourceNotFound);
      } else if (successCallback) {
        if (delayResultForSolr) {
          setTimeout(() => successCallback(this.toLongRunningAction(result.actions[0])), ERROR_NOTIFYING_SERVER_RESPONSE_DELAY);
        } else {
          successCallback(this.toLongRunningAction(result.actions[0]));
        }
      }
    };

    const notifyCallbackWrapped = (result: IGetActionInfoResult) => {
      notifyCallback && notifyCallback(this.toLongRunningAction(result.actions[0]));
    };

    const poller = new RequestPoller(request, isComplete, successCallbackWrapped, failureCallback, notifyCallbackWrapped);
    poller.startPolling();
    return poller;
  }

  /**
   * This function is a promise wrapper around trackAsyncJobProgress.
   * The returned promise is resolved when the LongRunningAction completes
   * and is rejected when the LongRunningAction fails.
   */
  public trackAsyncJobProgressWithPromise(
    actionId: string,
    notifyCallback?: (result: ILongRunningAction) => void,
    delayResultForSolr: boolean = true,
  ): Promise<ILongRunningAction> {
    return new Promise((resolve, reject) => {
      this.trackAsyncJobProgress(
        actionId,
        (longRunningAction: ILongRunningAction) => resolve(longRunningAction), // success
        (reason: ErrorReason) => reject(reason), // error
        notifyCallback,
        delayResultForSolr,
      );
    });
  }

  public getActionIdFromActionResult(result: IActionResult): Promise<string> {
    return new Promise((resolve, reject) => {
      if (result.actionId) {
        resolve(result.actionId);
      } else {
        reject(ErrorReason.ResourceNotFound);
      }
    });
  }

  public getActionResult<TResult extends IResult>(longRunningAction: ILongRunningAction): TResult {
    // Due to existing differences in API response format, it is possible that
    // longRunningAction.response is either IResponse type or IResult type.
    // If it's IReponse type, then there would be a property called 'result' to return,
    // otherwise longRunningAction.response can be returned directly.
    return longRunningAction.response?.result || longRunningAction.response;
  }

  // Convert a server result to an ILongRunningAction
  private toLongRunningAction(actionInfo: IActionInfo): ILongRunningAction {
    const action: ILongRunningAction = {
      id: actionInfo.id,
      type: this.StringToTypeDict[actionInfo.type],
      state: this.StringToStateDict[actionInfo.state],
      percentComplete: actionInfo.percentComplete,
      response: actionInfo.response,
      succeeded: actionInfo.succeeded
    };
    return action;
  }
}

export const LongRunningAction = new LongRunningActionService();
